import { type Dish } from "@fitness-app/data-models/entities/Dish";
import {
  DishIssueSource,
  IngredientIssueCategory,
  type DishIssueWithOwner,
  type TraineeDishIssueMetadata,
  type TraineeIngredientIssueMetadata,
} from "@fitness-app/data-models/entities/DishIssue";
import { type Ingredient } from "@fitness-app/data-models/entities/Ingredient";

export interface DishIssueTrainee {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

/** A row of the issues list: `dish` and `ingredient` are both embedded, one of them null. */
export type DishIssueListItem = DishIssueWithOwner & { trainee: DishIssueTrainee | null };

/** What a client's report is about - the `kind` of the `ingredient_issue_resolved` event. */
export type TraineeIssueKind = "product" | "dish";

export const INGREDIENT_ISSUE_CATEGORY_LABELS: Record<IngredientIssueCategory, string> = {
  [IngredientIssueCategory.Nutrients]: "Wartości odżywcze",
  [IngredientIssueCategory.Name]: "Nazwa lub marka",
  [IngredientIssueCategory.Photo]: "Zdjęcie",
  [IngredientIssueCategory.Duplicate]: "Duplikat",
  [IngredientIssueCategory.Other]: "Inne",
  [IngredientIssueCategory.Correction]: "Propozycja poprawek",
  [IngredientIssueCategory.Ingredients]: "Składniki lub ilości",
  [IngredientIssueCategory.Recipe]: "Przepis",
};

export const DISH_ISSUE_SOURCE_LABELS: Record<DishIssueSource, string> = {
  [DishIssueSource.Trainer]: "Trener",
  [DishIssueSource.Trainee]: "Klient",
};

type IssueMetadataSource = Pick<DishIssueListItem, "source" | "dishId" | "metadata">;

export const isTraineeIssue = (issue: Pick<DishIssueListItem, "source">): boolean =>
  issue.source === DishIssueSource.Trainee;

/** A client's report of a dish rather than a product: it carries `dishId` and no `ingredientId`. */
export const isTraineeDishIssue = (issue: Pick<DishIssueListItem, "source" | "dishId">): boolean =>
  isTraineeIssue(issue) && Boolean(issue.dishId);

export const getTraineeIssueKind = (issue: Pick<DishIssueListItem, "dishId">): TraineeIssueKind =>
  issue.dishId ? "dish" : "product";

export const getIssueDish = (issue: DishIssueListItem): Dish | null => ("dish" in issue ? (issue.dish ?? null) : null);

export const getIssueIngredient = (issue: DishIssueListItem): Ingredient | null =>
  "ingredient" in issue ? (issue.ingredient ?? null) : null;

const getTraineeMetadata = (issue: IssueMetadataSource): Record<string, unknown> | null =>
  isTraineeIssue(issue) && issue.metadata && typeof issue.metadata === "object" && !Array.isArray(issue.metadata)
    ? (issue.metadata as Record<string, unknown>)
    : null;

/** The metadata of a client's PRODUCT report; null for her dish reports and for trainer reports. */
export const getTraineeIssueMetadata = (issue: IssueMetadataSource): TraineeIngredientIssueMetadata | null =>
  isTraineeDishIssue(issue) ? null : (getTraineeMetadata(issue) as TraineeIngredientIssueMetadata | null);

/** The metadata of a client's DISH report; null for her product reports and for trainer reports. */
export const getTraineeDishIssueMetadata = (issue: IssueMetadataSource): TraineeDishIssueMetadata | null =>
  isTraineeDishIssue(issue) ? (getTraineeMetadata(issue) as TraineeDishIssueMetadata | null) : null;

export const getTraineeIssuePhotoUrl = (issue: IssueMetadataSource): string | null =>
  (getTraineeIssueMetadata(issue) ?? getTraineeDishIssueMetadata(issue))?.photoUrl || null;

export const getIssueName = (issue: DishIssueListItem): string =>
  getIssueDish(issue)?.name ||
  getIssueIngredient(issue)?.name ||
  getTraineeIssueMetadata(issue)?.ingredientName ||
  getTraineeDishIssueMetadata(issue)?.dishName ||
  "";

const formatDecimal = (value: number): string => String(Math.round(value * 10) / 10).replace(".", ",");

/**
 * What tells apart products with the same name ("Skyr pitny naturalny" comes from several brands):
 * brand, barcode and calories per 100 g.
 */
export const getProductDetailsLine = (
  product: Pick<Ingredient, "brand" | "barCode" | "nutrients"> & Partial<Pick<Ingredient, "unit">>,
): string =>
  [
    product.brand,
    product.barCode,
    typeof product.nutrients?.calories === "number"
      ? `${formatDecimal(product.nutrients.calories)} kcal / 100 ${product.unit || "g"}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

/** Macros per 100 g of a product, as the trainer compares them with the label. */
export const getProductNutrientsLine = (product: Pick<Ingredient, "nutrients">): string => {
  const { calories, protein, carbohydrates, fat } = product.nutrients ?? {};
  return [
    typeof calories === "number" ? `${formatDecimal(calories)} kcal` : null,
    typeof protein === "number" ? `B ${formatDecimal(protein)} g` : null,
    typeof carbohydrates === "number" ? `W ${formatDecimal(carbohydrates)} g` : null,
    typeof fat === "number" ? `T ${formatDecimal(fat)} g` : null,
  ]
    .filter(Boolean)
    .join(" · ");
};

/**
 * The photo for the list: the product's own, or the one the client proposes for a product that
 * has none yet - otherwise "Dodaj zdjęcie" proposals show an empty thumbnail.
 */
export const getIssueProductImage = (issue: DishIssueListItem): string | null =>
  getIssueIngredient(issue)?.image || getTraineeIssueMetadata(issue)?.proposedChanges?.image || null;

export const getTraineeName = (trainee: DishIssueTrainee | null): string =>
  trainee ? [trainee.firstName, trainee.lastName].filter(Boolean).join(" ") || trainee.email || "" : "";

export const hasProposedChanges = (issue: IssueMetadataSource): boolean =>
  Object.keys(getTraineeIssueMetadata(issue)?.proposedChanges ?? {}).length > 0;

export function getIngredientIssueLink(issueId: string): string {
  return `/nutrition/dishIssues?${new URLSearchParams({ issueId })}`;
}
